import { OrderSourceSummary, OrderSourceTotal } from '@types';
import AdminService from '_services/AdminService';
import React, { useCallback, useEffect, useState } from 'react';

const CHANNELS: { key: string; color: string }[] = [
  { key: 'Web', color: '#1d4ed8' },
  { key: 'iOS', color: '#111827' },
  { key: 'Android', color: '#16a34a' },
  { key: 'Mobile', color: '#7c3aed' },
  { key: 'Unknown', color: '#9ca3af' },
];

const PRODUCTS = [
  'Tickets',
  'Passes',
  'Parties',
  'Skating Lessons',
  'Hockey Lessons',
];

const formatMoney = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const channelTotal = (totals: OrderSourceTotal[], channel: string) =>
  totals.find(total => total.orderSource === channel) ?? {
    orderSource: channel,
    orderCount: 0,
    revenue: 0,
  };

const polar = (cx: number, cy: number, radius: number, angle: number) => [
  cx + radius * Math.cos(angle),
  cy + radius * Math.sin(angle),
];

const OrderSourcePie: React.FC<{ totals: OrderSourceTotal[] }> = ({
  totals,
}) => {
  const slices = CHANNELS.map(channel => ({
    ...channel,
    value: channelTotal(totals, channel.key).orderCount,
  })).filter(slice => slice.value > 0);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const size = 220;
  const center = size / 2;
  const radius = 88;

  let angle = -Math.PI / 2;
  const paths = slices.map(slice => {
    const sweep = (slice.value / total) * Math.PI * 2;
    const start = angle;
    const end = angle + sweep;
    angle = end;

    if (sweep >= Math.PI * 2 - 0.0001) {
      return { ...slice, d: '' };
    }

    const [x1, y1] = polar(center, center, radius, start);
    const [x2, y2] = polar(center, center, radius, end);
    const largeArc = sweep > Math.PI ? 1 : 0;
    return {
      ...slice,
      d: `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`,
    };
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Orders by channel"
    >
      {total === 0 ? (
        <circle cx={center} cy={center} r={radius} fill="#e5e7eb" />
      ) : paths.length === 1 && !paths[0].d ? (
        <circle cx={center} cy={center} r={radius} fill={paths[0].color} />
      ) : (
        paths.map(slice => (
          <path key={slice.key} d={slice.d} fill={slice.color} />
        ))
      )}
      <circle cx={center} cy={center} r={52} fill="white" />
      <text
        x={center}
        y={center - 6}
        textAnchor="middle"
        className="fill-gray-900"
        fontSize="22"
        fontWeight="700"
      >
        {totals.reduce((sum, item) => sum + item.orderCount, 0)}
      </text>
      <text
        x={center}
        y={center + 16}
        textAnchor="middle"
        className="fill-gray-500"
        fontSize="12"
      >
        orders
      </text>
    </svg>
  );
};

const OrderSources: React.FC = () => {
  const [summary, setSummary] = useState<OrderSourceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = (await AdminService.getOrderSourceSummary({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })) as unknown as OrderSourceSummary;
      setSummary(response);
    } catch (err) {
      console.error('Error fetching order sources:', err);
      setError('Unable to load order sources.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const totals = summary?.totals ?? [];
  const products = summary?.products ?? [];
  const orderTotal = totals.reduce((sum, item) => sum + item.orderCount, 0);

  const productCell = (product: string, channel: string) =>
    products.find(
      row => row.product === product && row.orderSource === channel,
    ) ?? { orderCount: 0, revenue: 0 };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Order Sources</h1>
        <p className="mt-1 text-sm text-gray-600">
          Where customer orders were placed this season. Earlier app orders are
          Mobile. Orders from before this tracking started are Unknown.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          Orders by channel
        </h2>
        {loading ? (
          <p className="mt-6 text-sm text-gray-500">Loading...</p>
        ) : (
          <div className="mt-6 flex flex-col md:flex-row md:items-center gap-8">
            <OrderSourcePie totals={totals} />
            <ul className="space-y-4 flex-1">
              {CHANNELS.map(channel => {
                const item = channelTotal(totals, channel.key);
                const share =
                  orderTotal === 0
                    ? 0
                    : Math.round((item.orderCount / orderTotal) * 100);
                return (
                  <li key={channel.key} className="flex items-start gap-3">
                    <span
                      className="mt-1 h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: channel.color }}
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {channel.key}{' '}
                        <span className="text-gray-500 font-normal">
                          {share}%
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {item.orderCount} orders · {formatMoney(item.revenue)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                {CHANNELS.map(channel => (
                  <th
                    key={channel.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {channel.key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {PRODUCTS.map(product => (
                <tr key={product}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {product}
                  </td>
                  {CHANNELS.map(channel => {
                    const cell = productCell(product, channel.key);
                    return (
                      <td
                        key={channel.key}
                        className="px-6 py-4 text-sm text-gray-600"
                      >
                        {cell.orderCount} · {formatMoney(cell.revenue)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrderSources;
